import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useCurrentUser } from "../../modules/authentication/useCurrentUser";
import { useSearchParams } from "react-router-dom";
import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import {
  useDashboardCustomersLazyQuery,
  useDashboardStatsLazyQuery,
  useGetCustomerOrganisationsLazyQuery,
} from "../../graphql/generated/hooks";
import { fetchAllPages, useFetchAll } from "../../hooks/useFetchAll";
import { FilterPanel } from "../../components/dashboard/FilterPanel";
import { SensorYield } from "@components/dashboard/metrics/SensorYield";
import { TestCountChart } from "@components/dashboard/metrics/TestCountChart";
import { SoilTestMap } from "@components/dashboard/metrics/SoilTestMap";
import SegmentedControl from "@components/dashboard/SegmentedControl";
import {
  differenceInCalendarDays,
  endOfDay,
  format,
  startOfDay,
  sub,
} from "date-fns";
import OrganisationUsage from "@components/dashboard/metrics/OrganisationUsage";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Badge } from "@components/ui/badge";
import { PasswordSetupBanner } from "@modules/authentication/PasswordSetupBanner";
import { Switch } from "@components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@components/ui/popover";
import { Calendar } from "@components/ui/calendar";
import { Label } from "@components/ui/label";
import { DateRange } from "react-day-picker";
import {
  Calendar as CalendarIcon,
  Filter as FilterIcon,
  X,
} from "lucide-react";

type RangeOption = "custom" | "60d" | "30d" | "7d" | "24h";

const Dashboard: React.FC = () => {
  const { currentUser } = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeRange, setActiveRange] = useState<RangeOption>(() => {
    const rangeParam = searchParams.get("range");
    if (
      rangeParam &&
      ["custom", "60d", "30d", "7d", "24h"].includes(rangeParam)
    ) {
      return rangeParam as RangeOption;
    }
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");
    if (fromParam && toParam) {
      return "custom";
    }
    return "30d";
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isPesExcluded, setIsPesExcluded] = useState<boolean>(false);
  const [pesActualCustomerId, setPesActualCustomerId] = useState<
    string | undefined
  >(undefined);
  const [includedCustomers, setIncludedCustomers] = useState<string[]>(() => {
    const customers = searchParams.get("includeCustomers");
    return customers ? customers.split(",").filter(Boolean) : [];
  });
  const [excludedCustomers, setExcludedCustomers] = useState<string[]>(() => {
    const customers = searchParams.get("excludeCustomers");
    return customers ? customers.split(",").filter(Boolean) : [];
  });
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");

    const to = toParam ? new Date(toParam) : new Date();
    const from = fromParam
      ? new Date(fromParam)
      : (() => {
          const defaultFrom = new Date();
          defaultFrom.setMonth(defaultFrom.getMonth() - 1);
          return defaultFrom;
        })();

    return { from, to };
  });

  const [fetchDashboardStats] = useDashboardStatsLazyQuery({
    fetchPolicy: "network-only",
  });
  const [fetchDashboardCustomers] = useDashboardCustomersLazyQuery({
    fetchPolicy: "network-only",
  });
  const [fetchCustomerOrgs] = useGetCustomerOrganisationsLazyQuery({
    fetchPolicy: "network-only",
  });
  const {
    data: dashboardCustomers,
    loading: dashboardCustomersLoading,
    error: dashboardCustomersError,
  } = useFetchAll(fetchDashboardCustomers, (data) => data.customers, {
    fetchPolicy: "network-only",
    variables: { after: null },
  });
  const [customersMap, setCustomersMap] = useState<
    Map<string, { id: string; name: string; orgIds: string[] }>
  >(new Map());
  const [customerOrganisationsLoading, setCustomerOrganisationsLoading] =
    useState(true);
  const customersLoading =
    dashboardCustomersLoading || customerOrganisationsLoading;

  useEffect(() => {
    if (dashboardCustomersLoading) {
      return;
    }

    let disposed = false;
    const result = new Map<
      string,
      { id: string; name: string; orgIds: string[] }
    >();

    const fetchCustomerOrganisations = async () => {
      setCustomerOrganisationsLoading(true);

      try {
        for (const customer of dashboardCustomers) {
          const organisations = await fetchAllPages(
            fetchCustomerOrgs,
            (data) => data.customer?.organisations,
            {
              fetchPolicy: "network-only",
              variables: { id: customer.id, after: null },
            }
          );

          result.set(customer.id, {
            id: customer.id,
            name: customer.name,
            orgIds: organisations.map((organisation) => organisation.id),
          });
        }

        if (!disposed) {
          setCustomersMap(result);
        }
      } catch (error) {
        console.error("Failed to load customers", error);
      } finally {
        if (!disposed) {
          setCustomerOrganisationsLoading(false);
        }
      }
    };

    void fetchCustomerOrganisations();

    return () => {
      disposed = true;
    };
  }, [dashboardCustomers, dashboardCustomersLoading, fetchCustomerOrgs]);

  useEffect(() => {
    if (dashboardCustomersError) {
      console.error("Failed to load customers", dashboardCustomersError);
    }
  }, [dashboardCustomersError]);

  useEffect(() => {
    const customers = searchParams.get("includeCustomers");
    setIncludedCustomers(customers ? customers.split(",").filter(Boolean) : []);
  }, [searchParams]);

  useEffect(() => {
    const customers = searchParams.get("excludeCustomers");
    setExcludedCustomers(customers ? customers.split(",").filter(Boolean) : []);
  }, [searchParams]);

  useEffect(() => {
    setSearchParams((params) => {
      if (dateRange.from) {
        params.set("from", dateRange.from.toISOString());
      } else {
        params.delete("from");
      }
      if (dateRange.to) {
        params.set("to", dateRange.to.toISOString());
      } else {
        params.delete("to");
      }
      return params;
    });
  }, [dateRange, setSearchParams]);

  useEffect(() => {
    setSearchParams((params) => {
      params.set("range", activeRange);
      return params;
    });
  }, [activeRange, setSearchParams]);

  const expandCustomerIdsToOrgIds = useCallback(
    (customerIds: string[]) => {
      return customerIds.flatMap((id) => customersMap.get(id)?.orgIds ?? []);
    },
    [customersMap]
  );

  const includedOrgIds = useMemo(
    () => expandCustomerIdsToOrgIds(includedCustomers),
    [includedCustomers, expandCustomerIdsToOrgIds]
  );
  const excludedOrgIds = useMemo(
    () => expandCustomerIdsToOrgIds(excludedCustomers),
    [excludedCustomers, expandCustomerIdsToOrgIds]
  );

  const includedOrgIdsParam = useMemo(
    () => includedOrgIds.slice().sort().join(","),
    [includedOrgIds]
  );
  const excludedOrgIdsParam = useMemo(
    () => excludedOrgIds.slice().sort().join(","),
    [excludedOrgIds]
  );

  const customerOptions = useMemo(() => {
    return [...customersMap.values()].map((customer) => ({
      label: customer.name,
      value: customer.id,
    }));
  }, [customersMap]);

  useEffect(() => {
    const pesOption = customerOptions.find((option) => option.label === "PES");
    setPesActualCustomerId(pesOption?.value);
  }, [customerOptions]);

  useEffect(() => {
    if (!pesActualCustomerId) {
      setIsPesExcluded(false);
      return;
    }

    setIsPesExcluded(excludedCustomers.includes(pesActualCustomerId));
  }, [excludedCustomers, pesActualCustomerId]);

  const [dashboardData, setDashboardData] = useState<{
    currentPeriod: Array<{
      date: string;
      totalCount: number;
      soilTests: number;
      successfulSensorChecks: number;
      failedSensorChecks: number;
    }>;
    previousPeriod: Array<{
      date: string;
      totalCount: number;
      soilTests: number;
      successfulSensorChecks: number;
      failedSensorChecks: number;
    }>;
    organisationStats: Array<{
      organisationId: string;
      organisationName: string;
      soilTests: number;
      successfulSensorChecks: number;
      failedSensorChecks: number;
    }>;
    mapPoints: Array<{
      testId: string;
      latitude: number;
      longitude: number;
      what3Words?: string | null;
      testName?: string | null;
      organisationName?: string | null;
    }>;
  } | null>(null);

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) {
      setDashboardData(null);
      return;
    }

    let ignore = false;
    const rangeFrom = startOfDay(dateRange.from);
    const rangeTo = startOfDay(dateRange.to);
    const spanDays = differenceInCalendarDays(rangeTo, rangeFrom) + 1;
    const previousPeriodStart = sub(rangeFrom, { days: spanDays });
    const previousPeriodEnd = sub(rangeFrom, { days: 1 });

    setMetricsLoading(true);
    setMetricsError(null);

    const included = includedOrgIdsParam ? includedOrgIdsParam.split(",") : [];
    const excluded = excludedOrgIdsParam ? excludedOrgIdsParam.split(",") : [];

    fetchDashboardStats({
      variables: {
        startDate: rangeFrom.toISOString(),
        endDate: endOfDay(rangeTo).toISOString(),
        previousStartDate: previousPeriodStart.toISOString(),
        previousEndDate: endOfDay(previousPeriodEnd).toISOString(),
        includedOrganisationIds: included.length > 0 ? included : null,
        excludedOrganisationIds: excluded.length > 0 ? excluded : null,
      },
    })
      .then(({ data }) => {
        if (ignore) {
          return;
        }
        setDashboardData(data?.dashboardStats ?? null);
      })
      .catch((error) => {
        if (ignore) {
          return;
        }
        console.error("Failed to load dashboard metrics", error);
        setDashboardData(null);
        setMetricsError("Unable to load dashboard analytics.");
      })
      .finally(() => {
        if (!ignore) {
          setMetricsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [
    dateRange.from,
    dateRange.to,
    includedOrgIdsParam,
    excludedOrgIdsParam,
    fetchDashboardStats,
  ]);

  const currentPeriodData = useMemo(
    () =>
      dashboardData?.currentPeriod?.map((d) => ({
        date: new Date(d.date),
        count: d.totalCount,
        failedSensorChecks: d.failedSensorChecks,
        successfulSensorChecks: d.successfulSensorChecks,
        soilTests: d.soilTests,
      })) ?? [],
    [dashboardData]
  );

  const previousPeriodData = useMemo(
    () =>
      dashboardData?.previousPeriod?.map((d) => ({
        date: new Date(d.date),
        count: d.totalCount,
        failedSensorChecks: d.failedSensorChecks,
        successfulSensorChecks: d.successfulSensorChecks,
        soilTests: d.soilTests,
      })) ?? [],
    [dashboardData]
  );

  const organisationTestCounts = useMemo(
    () =>
      (dashboardData?.organisationStats ?? [])
        .map((o) => {
          const totalSensorChecks =
            o.successfulSensorChecks + o.failedSensorChecks;
          return {
            id: o.organisationId,
            name: o.organisationName,
            count: o.soilTests,
            yieldPercentage:
              totalSensorChecks === 0
                ? 0
                : Math.round(
                    (o.successfulSensorChecks / totalSensorChecks) * 100
                  ),
            totalSensorChecks,
            failedSensorChecks: o.failedSensorChecks,
          };
        })
        .sort((a, b) => b.count - a.count),
    [dashboardData]
  );

  const sensorYieldCurrentPeriodData = useMemo(
    () =>
      (dashboardData?.currentPeriod ?? []).map((d) => ({
        date: d.date,
        failedSensorChecks: d.failedSensorChecks,
        successfulSensorChecks: d.successfulSensorChecks,
      })),
    [dashboardData]
  );

  const sensorYieldPreviousPeriodData = useMemo(
    () =>
      (dashboardData?.previousPeriod ?? []).map((d) => ({
        date: d.date,
        failedSensorChecks: d.failedSensorChecks,
        successfulSensorChecks: d.successfulSensorChecks,
      })),
    [dashboardData]
  );

  const mapLocations = useMemo(
    () =>
      (dashboardData?.mapPoints ?? []).map((p) => ({
        id: p.testId,
        latitude: p.latitude,
        longitude: p.longitude,
        name: p.testName ?? null,
        organisationName: p.organisationName ?? null,
        what3words: p.what3Words ?? null,
      })),
    [dashboardData]
  );

  const handleCustomerFilterChange = useCallback(
    (included: string[], excluded: string[]) => {
      setIncludedCustomers(included);
      setExcludedCustomers(excluded);
      setSearchParams((params) => {
        if (included.length > 0) {
          params.set("includeCustomers", included.join(","));
        } else {
          params.delete("includeCustomers");
        }
        if (excluded.length > 0) {
          params.set("excludeCustomers", excluded.join(","));
        } else {
          params.delete("excludeCustomers");
        }
        return params;
      });
    },
    [setSearchParams]
  );

  const handlePesExcludeToggle = (nowExcludingPes: boolean) => {
    if (!pesActualCustomerId) {
      return;
    }

    let nextIncluded = [...includedCustomers];
    let nextExcluded = [...excludedCustomers];

    if (nowExcludingPes) {
      if (!nextExcluded.includes(pesActualCustomerId)) {
        nextExcluded.push(pesActualCustomerId);
      }
      nextIncluded = nextIncluded.filter((id) => id !== pesActualCustomerId);
    } else {
      nextExcluded = nextExcluded.filter((id) => id !== pesActualCustomerId);
    }

    handleCustomerFilterChange(nextIncluded, nextExcluded);
    setIsPesExcluded(nowExcludingPes);
  };

  const openDatePicker = () => {
    setIsCalendarOpen(true);
  };

  const handleRangeSelect = (range: RangeOption) => {
    setActiveRange(range);

    if (range === "custom") {
      setIsCalendarOpen(true);
      return;
    }

    setIsCalendarOpen(false);

    const now = new Date();
    let from = new Date();

    switch (range) {
      case "60d":
        from.setDate(from.getDate() - 60);
        break;
      case "30d":
        from.setDate(from.getDate() - 30);
        break;
      case "7d":
        from.setDate(from.getDate() - 7);
        break;
      case "24h": {
        const utcYear = now.getUTCFullYear();
        const utcMonth = now.getUTCMonth();
        const utcDay = now.getUTCDate();
        from = new Date(Date.UTC(utcYear, utcMonth, utcDay, 0, 0, 0, 0));
        break;
      }
      default:
        break;
    }

    setDateRange({ from, to: now });
  };

  const dateRangeLabel = useMemo(() => {
    if (dateRange.from && dateRange.to) {
      const fromLabel = format(dateRange.from, "MMM d, yyyy");
      const toLabel = format(dateRange.to, "MMM d, yyyy");
      return `${fromLabel} - ${toLabel}`;
    }

    if (dateRange.from) {
      return format(dateRange.from, "MMM d, yyyy");
    }

    return "Select dates";
  }, [dateRange]);

  const handleCalendarSelect = (selectedRange: DateRange | undefined) => {
    if (!selectedRange?.from) {
      return;
    }

    setDateRange({
      from: selectedRange.from,
      to: selectedRange.to ?? selectedRange.from,
    });
    setActiveRange("custom");
  };

  const totalFilters = includedCustomers.length + excludedCustomers.length;
  const firstName = currentUser?.fullName
    ? currentUser.fullName.split(" ")[0]
    : "User";

  const dashboardTitle = (
    <div className="flex items-center gap-3">
      {currentUser?.profilePictureUrl ? (
        <img
          src={currentUser.profilePictureUrl}
          alt={currentUser.fullName ?? ""}
          className="size-10 rounded-full object-cover"
        />
      ) : (
        <div className="flex size-10 items-center justify-center rounded-full bg-muted text-sm font-semibold uppercase text-muted-foreground">
          {firstName.charAt(0)}
        </div>
      )}
      <span className="text-base font-semibold">Welcome back, {firstName}</span>
    </div>
  );

  return (
    <PageTemplate
      title={dashboardTitle}
      browserTitle={`Welcome back, ${firstName}`}
      activeItem="Admin"
      activeSubItem="Dashboard"
    >
      <div className="flex flex-col gap-6">
        <PasswordSetupBanner />
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <SegmentedControl
            activeRange={activeRange}
            onRangeSelect={handleRangeSelect}
            onCustomClick={openDatePicker}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Popover
              open={isCalendarOpen}
              onOpenChange={(open) => {
                setIsCalendarOpen(open);
                if (open) {
                  setActiveRange("custom");
                }
              }}
            >
              <PopoverTrigger>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                  onClick={() => setIsCalendarOpen(true)}
                >
                  <CalendarIcon className="size-4" />
                  <span className="whitespace-nowrap">{dateRangeLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={dateRange}
                  defaultMonth={dateRange.from ?? new Date()}
                  onSelect={handleCalendarSelect}
                  showOutsideDays={false}
                />
              </PopoverContent>
            </Popover>

            <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
              <PopoverTrigger>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2"
                >
                  <FilterIcon className="size-4" />
                  <span>Filters ({totalFilters})</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-0" align="end">
                <FilterPanel
                  customerOptions={customerOptions}
                  includedCustomers={includedCustomers}
                  excludedCustomers={excludedCustomers}
                  onChange={handleCustomerFilterChange}
                  onClose={() => setIsFilterOpen(false)}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="exclude-pes"
              checked={isPesExcluded}
              onCheckedChange={(checked) =>
                handlePesExcludeToggle(Boolean(checked))
              }
            />
            <Label htmlFor="exclude-pes">Exclude PES</Label>
          </div>
          {includedCustomers.length > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Included customers: {includedCustomers.length}</span>
              <button
                type="button"
                onClick={() =>
                  handleCustomerFilterChange([], excludedCustomers)
                }
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Clear included customers"
              >
                <X className="size-3.5" />
              </button>
            </Badge>
          )}
          {excludedCustomers.length > 0 && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <span>Excluded customers: {excludedCustomers.length}</span>
              <button
                type="button"
                onClick={() =>
                  handleCustomerFilterChange(includedCustomers, [])
                }
                className="text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Clear excluded customers"
              >
                <X className="size-3.5" />
              </button>
            </Badge>
          )}
        </div>

        {metricsError && (
          <Card className="border-destructive/30 bg-destructive/5">
            <CardContent className="pt-6">
              <p className="text-sm text-destructive">{metricsError}</p>
            </CardContent>
          </Card>
        )}

        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <Card className="h-full pb-0">
            <CardHeader className="pb-0">
              <CardTitle>Test count</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col px-6 pb-6 pt-0">
              <TestCountChart
                currentPeriodData={currentPeriodData}
                previousPeriodData={previousPeriodData}
                loading={metricsLoading}
              />
            </CardContent>
          </Card>
          <Card className="h-full pb-0">
            <CardHeader className="pb-0">
              <CardTitle>Sensor yield</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col px-6 pb-6 pt-0">
              <SensorYield
                currentPeriodData={sensorYieldCurrentPeriodData}
                previousPeriodData={sensorYieldPreviousPeriodData}
                loading={metricsLoading}
              />
            </CardContent>
          </Card>
          <Card className="lg:col-span-2 pb-0">
            <CardHeader className="pb-0">
              <CardTitle>Map</CardTitle>
            </CardHeader>
            <CardContent className="h-[420px] overflow-hidden px-0 pb-0 pt-0">
              <SoilTestMap
                locations={mapLocations}
                loading={metricsLoading || customersLoading}
                excludedOrgs={excludedOrgIds}
              />
            </CardContent>
          </Card>
          <Card className="lg:col-span-2 pb-0">
            <CardHeader className="pb-0">
              <CardTitle>Usage by organisation</CardTitle>
            </CardHeader>
            <CardContent className="max-h-[420px] px-6 pb-6 pt-0">
              <OrganisationUsage
                organisationsData={organisationTestCounts}
                loading={metricsLoading}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </PageTemplate>
  );
};

export default Dashboard;
